import React, { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Loader2, Wrench } from "lucide-react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { useToast } from "@/components/ui/use-toast";
import { EmptyState } from "@/components/ui/empty-state";

interface MaintenanceLogSheetProps {
  equipmentId: Id<"equipment">;
  isOpen: boolean;
  onClose: () => void;
}

export function MaintenanceLogSheet({ equipmentId, isOpen, onClose }: MaintenanceLogSheetProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLog, setEditingLog] = useState<Id<"equipmentMaintenanceLog"> | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [logToDelete, setLogToDelete] = useState<Id<"equipmentMaintenanceLog"> | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formData, setFormData] = useState({
    maintenanceDate: "",
    maintenanceType: "Routine Maintenance" as any,
    description: "",
    performedBy: "",
    cost: "",
    nextMaintenanceDate: "",
    notes: "",
  });
  const { toast } = useToast();

  const equipment = useQuery(api.equipment.getById, { id: equipmentId });
  const maintenanceLogs =
    useQuery(api.equipmentMaintenanceLog.getByEquipment, { equipmentId }) || [];
  const createMaintenanceLog = useMutation(api.equipmentMaintenanceLog.create);
  const updateMaintenanceLog = useMutation(api.equipmentMaintenanceLog.update);
  const removeMaintenanceLog = useMutation(api.equipmentMaintenanceLog.remove);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.maintenanceDate || !formData.description || !formData.performedBy) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      if (editingLog) {
        // Update existing log
        await updateMaintenanceLog({
          id: editingLog,
          maintenanceDate: formData.maintenanceDate,
          maintenanceType: formData.maintenanceType,
          description: formData.description,
          performedBy: formData.performedBy,
          cost: formData.cost ? parseFloat(formData.cost) : undefined,
          nextMaintenanceDate: formData.nextMaintenanceDate || undefined,
          notes: formData.notes || undefined,
        });

        toast({
          title: "Success!",
          description: "Maintenance log updated successfully",
        });
      } else {
        // Create new log
        await createMaintenanceLog({
          equipmentId,
          maintenanceDate: formData.maintenanceDate,
          maintenanceType: formData.maintenanceType,
          description: formData.description,
          performedBy: formData.performedBy,
          cost: formData.cost ? parseFloat(formData.cost) : undefined,
          nextMaintenanceDate: formData.nextMaintenanceDate || undefined,
          notes: formData.notes || undefined,
        });

        toast({
          title: "Success!",
          description: "Maintenance log added successfully",
        });
      }

      // Reset form
      setFormData({
        maintenanceDate: "",
        maintenanceType: "Routine Maintenance",
        description: "",
        performedBy: "",
        cost: "",
        nextMaintenanceDate: "",
        notes: "",
      });
      setShowAddForm(false);
      setEditingLog(null);
    } catch (error) {
      toast({
        title: "Error",
        description: editingLog
          ? "Failed to update maintenance log"
          : "Failed to add maintenance log",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (log: any) => {
    setEditingLog(log._id);
    setFormData({
      maintenanceDate: log.maintenanceDate,
      maintenanceType: log.maintenanceType,
      description: log.description,
      performedBy: log.performedBy,
      cost: log.cost?.toString() || "",
      nextMaintenanceDate: log.nextMaintenanceDate || "",
      notes: log.notes || "",
    });
    setShowAddForm(true);
  };

  const handleCancelEdit = () => {
    setEditingLog(null);
    setFormData({
      maintenanceDate: "",
      maintenanceType: "Routine Maintenance",
      description: "",
      performedBy: "",
      cost: "",
      nextMaintenanceDate: "",
      notes: "",
    });
    setShowAddForm(false);
  };

  const handleDeleteClick = (logId: Id<"equipmentMaintenanceLog">) => {
    setLogToDelete(logId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!logToDelete) return;

    setIsDeleting(true);
    try {
      await removeMaintenanceLog({ id: logToDelete });
      toast({
        title: "Success!",
        description: "Maintenance log deleted successfully",
      });
      setDeleteDialogOpen(false);
      setLogToDelete(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete maintenance log",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const formatCurrency = (amount: number | undefined) => {
    if (!amount) return "-";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getMaintenanceTypeBadgeVariant = (type: string) => {
    switch (type) {
      case "Routine Maintenance":
        return "default";
      case "Repair":
        return "destructive";
      case "Inspection":
        return "secondary";
      default:
        return "outline";
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[900px] sm:max-w-[900px]">
        <SheetHeader>
          <SheetTitle>Maintenance Log - {equipment?.name}</SheetTitle>
          <SheetDescription>
            Track maintenance history and add new maintenance records.
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <div className="space-y-6 py-6">
            {/* Equipment Info */}
            <div className="rounded-lg bg-gray-50 p-4">
              <h3 className="mb-2 font-medium">Equipment Information</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Brand:</span> {equipment?.brand}
                </div>
                <div>
                  <span className="text-gray-500">Model:</span> {equipment?.model}
                </div>
                <div>
                  <span className="text-gray-500">Serial Number:</span>{" "}
                  {equipment?.serialNumber || "-"}
                </div>
                <div>
                  <span className="text-gray-500">Status:</span> {equipment?.status}
                </div>
              </div>
            </div>

            {/* Add New Maintenance Record */}
            <div>
              <div className="mb-4 flex items-center justify-between">
                <h3 className="font-medium">Maintenance Records</h3>
                <Button
                  onClick={() => {
                    if (editingLog) {
                      handleCancelEdit();
                    } else {
                      setShowAddForm(!showAddForm);
                    }
                  }}
                  className="gap-2"
                  size="sm"
                >
                  <Plus className="h-4 w-4" />
                  Add Record
                </Button>
              </div>

              {showAddForm && (
                <div className="mb-4 rounded-lg border p-4">
                  <h4 className="mb-4 font-medium">
                    {editingLog ? "Edit Maintenance Record" : "Add New Maintenance Record"}
                  </h4>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="maintenanceDate">
                          Maintenance Date <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="maintenanceDate"
                          type="date"
                          value={formData.maintenanceDate}
                          onChange={(e) => handleInputChange("maintenanceDate", e.target.value)}
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="maintenanceType">
                          Maintenance Type <span className="text-red-500">*</span>
                        </Label>
                        <Select
                          value={formData.maintenanceType}
                          onValueChange={(value) => handleInputChange("maintenanceType", value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Routine Maintenance">Routine Maintenance</SelectItem>
                            <SelectItem value="Repair">Repair</SelectItem>
                            <SelectItem value="Inspection">Inspection</SelectItem>
                            <SelectItem value="Cleaning">Cleaning</SelectItem>
                            <SelectItem value="Calibration">Calibration</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description">
                        Description <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => handleInputChange("description", e.target.value)}
                        placeholder="Describe the maintenance performed"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="performedBy">
                          Performed By <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="performedBy"
                          value={formData.performedBy}
                          onChange={(e) => handleInputChange("performedBy", e.target.value)}
                          placeholder="Name or company"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="cost">Cost</Label>
                        <Input
                          id="cost"
                          type="number"
                          step="0.01"
                          value={formData.cost}
                          onChange={(e) => handleInputChange("cost", e.target.value)}
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="nextMaintenanceDate">Next Maintenance Date</Label>
                      <Input
                        id="nextMaintenanceDate"
                        type="date"
                        value={formData.nextMaintenanceDate}
                        onChange={(e) => handleInputChange("nextMaintenanceDate", e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="notes">Notes</Label>
                      <Textarea
                        id="notes"
                        value={formData.notes}
                        onChange={(e) => handleInputChange("notes", e.target.value)}
                        placeholder="Additional notes or observations"
                      />
                    </div>

                    <div className="flex gap-3">
                      <Button type="submit">{editingLog ? "Update Record" : "Add Record"}</Button>
                      <Button type="button" variant="outline" onClick={handleCancelEdit}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                </div>
              )}
            </div>

            <Separator />

            {/* Maintenance History */}
            <div>
              <h3 className="mb-4 font-medium">Maintenance History</h3>
              {maintenanceLogs.length === 0 ? (
                <EmptyState
                  icon={Wrench}
                  title="No maintenance records"
                  description="Add the first record to get started."
                />
              ) : (
                <div className="overflow-x-auto">
                  <Table className="min-w-full table-auto">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Performed By</TableHead>
                        <TableHead>Cost</TableHead>
                        <TableHead>Next Due</TableHead>
                        <TableHead>Notes</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {maintenanceLogs.map((log: any) => (
                        <TableRow key={log._id}>
                          <TableCell>{formatDate(log.maintenanceDate)}</TableCell>
                          <TableCell>
                            <Badge variant={getMaintenanceTypeBadgeVariant(log.maintenanceType)}>
                              {log.maintenanceType}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-[150px]">
                            <div className="truncate" title={log.description}>
                              {log.description}
                            </div>
                          </TableCell>
                          <TableCell>{log.performedBy}</TableCell>
                          <TableCell>{formatCurrency(log.cost)}</TableCell>
                          <TableCell>
                            {log.nextMaintenanceDate ? formatDate(log.nextMaintenanceDate) : "-"}
                          </TableCell>
                          <TableCell className="max-w-[120px]">
                            <div className="truncate" title={log.notes || "-"}>
                              {log.notes || "-"}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(log)}
                                className="h-8 w-8 p-0"
                                aria-label="Edit maintenance record"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteClick(log._id)}
                                className="h-8 w-8 p-0 text-red-500 hover:bg-red-50 hover:text-red-600"
                                aria-label="Delete maintenance record"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        </ScrollArea>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure you want to delete?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete this maintenance record
                and remove it from our servers.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
}
